import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';



const initialState = {
  renderJob: null,
  isLoading: false,
  error: null,
  batchJobs: [],
  batchProgress: {},
  exportJobs: [],
  exportProgress: {},
  downloadProgress: 0,
};

// Enhanced cloud render for export with specific parameters (synchronous)
export const renderPolotnoExport = createAsyncThunk(
  'polotno/renderExport',
  async ({ designJson, format, dpi, quality, pixelRatio = 1, fonts = [] }, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      if (!apiKey) {
        throw new Error('Polotno API key not found');
      }

      const url = `https://api.polotno.com/api/renders?KEY=${apiKey}`;

      // Process design JSON to handle custom fonts

      const requestBody = {
        design: designJson,
        pixelRatio: pixelRatio,
        format: format, // 'jpeg', 'png', 'pdf'
        dpi: parseInt(dpi) || 300,
        quality: quality || 0.9,
        fonts: fonts
      };

      const req = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Prefer': 'wait' // Synchronous request - wait for completion
        },
        body: JSON.stringify(requestBody),
      });

      const job = await req.json();

      if (job.status === 'error') {
        return rejectWithValue(job);
      }

      // For synchronous requests, job should be done immediately
      if (job.status === 'done' && job.output) {
        return job;
      } else {
        return rejectWithValue('Synchronous render failed - job not completed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to render export');
    }
  }
);

// Batch export for multiple designs (synchronous)
export const renderBatchExports = createAsyncThunk(
  'polotno/renderBatchExports',
  async ({ designs, format, dpi, quality, pixelRatio = 1, fonts = [] }, { dispatch, rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      if (!apiKey) {
        throw new Error('Polotno API key not found');
      }

      const url = `https://api.polotno.com/api/renders?KEY=${apiKey}`;

      const jobs = await Promise.all(
        designs.map(async (design, index) => {
          // Process design for cloud rendering with font handling

          // design = { ...design }
          design = {
            ...design,
            fonts
          }

          const requestBody = {
            design: design,
            pixelRatio: pixelRatio,
            format: format,
            dpi: parseInt(dpi) || 300,
            quality: quality || 0.9,
            fonts: fonts
          };

          const req = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Prefer': 'wait' // Synchronous request - wait for completion
            },
            body: JSON.stringify(requestBody),
          });

          const job = await req.json();

          // For synchronous requests, job should be done immediately
          if (job.status === 'done' && job.output) {
            return { ...job, index, designId: design.id };
          } else {
            throw new Error(`Synchronous render failed for design ${index}`);
          }
        })
      );

      return jobs;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to render batch exports');
    }
  }
);

// Check render status with enhanced error handling
export const checkRenderStatus = createAsyncThunk(
  'polotno/checkRenderStatus',
  async (jobId, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      if (!apiKey) {
        throw new Error('Polotno API key not found');
      }

      const url = `https://api.polotno.com/api/renders/${jobId}?KEY=${apiKey}`;
      const req = await fetch(url);

      if (!req.ok) {
        throw new Error(`HTTP error! status: ${req.status}`);
      }

      const job = await req.json();
      return job;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to check render status');
    }
  }
);

// Download rendered file
export const downloadRenderedFile = createAsyncThunk(
  'polotno/downloadRenderedFile',
  async ({ jobId, filename }, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      if (!apiKey) {
        throw new Error('Polotno API key not found');
      }

      // First check the job status to get the output URL
      const statusUrl = `https://api.polotno.com/api/renders/${jobId}?KEY=${apiKey}`;
      const statusReq = await fetch(statusUrl);

      if (!statusReq.ok) {
        throw new Error(`HTTP error! status: ${statusReq.status}`);
      }

      const statusResult = await statusReq.json();

      if (statusResult.status === 'done' && statusResult.output) {
        // Download from the output URL
        const downloadReq = await fetch(statusResult.output);

        if (!downloadReq.ok) {
          throw new Error(`HTTP error! status: ${downloadReq.status}`);
        }

        const blob = await downloadReq.blob();
        return { blob, filename };
      } else {
        throw new Error('Job not completed or no output URL available');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download rendered file');
    }
  }
);

// Legacy functions for backward compatibility
export const renderPolotnoDesign = createAsyncThunk(
  'polotno/renderDesign',
  async (designJson, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      const url = `https://api.polotno.com/api/renders?KEY=${apiKey}`;
      const req = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          design: designJson,
          pixelRatio: 1,
        }),
      });
      const job = await req.json();
      if (job.status === 'error') {
        return rejectWithValue(job);
      }
      return job;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to render design');
    }
  }
);

export const renderBatchDesigns = createAsyncThunk(
  'polotno/renderBatchDesigns',
  async (designs, { dispatch, rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
      const url = `https://api.polotno.com/api/renders?KEY=${apiKey}`;

      const jobs = await Promise.all(
        designs.map(async (design) => {
          const req = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              design: design,
              pixelRatio: 1,
            }),
          });
          return req.json();
        })
      );

      return jobs;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to render batch designs');
    }
  }
);

const polotnoSlice = createSlice({
  name: 'polotno',
  initialState,
  reducers: {
    clearBatchJobs: (state) => {
      state.batchJobs = [];
      state.batchProgress = {};
    },
    clearExportJobs: (state) => {
      state.exportJobs = [];
      state.exportProgress = {};
      state.downloadProgress = 0;
    },
    updateBatchProgress: (state, action) => {
      const { jobId, progress } = action.payload;
      state.batchProgress[jobId] = progress;
    },
    updateExportProgress: (state, action) => {
      const { jobId, progress } = action.payload;
      state.exportProgress[jobId] = progress;
    },
    updateDownloadProgress: (state, action) => {
      state.downloadProgress = action.payload;
    },
    resetDownloadProgress: (state) => {
      state.downloadProgress = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Enhanced export render
      .addCase(renderPolotnoExport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.renderJob = null;
      })
      .addCase(renderPolotnoExport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.renderJob = action.payload;
      })
      .addCase(renderPolotnoExport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Batch export
      .addCase(renderBatchExports.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.exportJobs = [];
        state.exportProgress = {};
      })
      .addCase(renderBatchExports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exportJobs = action.payload;
      })
      .addCase(renderBatchExports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Download rendered file
      .addCase(downloadRenderedFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadRenderedFile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Trigger download
        const { blob, filename } = action.payload;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .addCase(downloadRenderedFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Legacy cases for backward compatibility
      .addCase(renderPolotnoDesign.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.renderJob = null;
      })
      .addCase(renderPolotnoDesign.fulfilled, (state, action) => {
        state.isLoading = false;
        state.renderJob = action.payload;
      })
      .addCase(renderPolotnoDesign.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(renderBatchDesigns.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.batchJobs = [];
        state.batchProgress = {};
      })
      .addCase(renderBatchDesigns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.batchJobs = action.payload;
      })
      .addCase(renderBatchDesigns.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBatchJobs,
  clearExportJobs,
  updateBatchProgress,
  updateExportProgress,
  updateDownloadProgress,
  resetDownloadProgress
} = polotnoSlice.actions;
export default polotnoSlice.reducer; 